import type React from 'react'
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'

// ── Types ──────────────────────────────────────────────────────────

export type Theme = 'light' | 'dark' | 'system'
export type Palette = 'brand' | 'default'
export type Contrast = 'regular' | 'high'
export type TextSize = 'small' | 'medium' | 'large'

interface ThemeContextType {
	// Dark mode
	darkMode: boolean
	toggleDarkMode: () => void
	theme: Theme
	setTheme: (t: Theme) => void
	// Palette
	palette: Palette
	setPalette: (palette: Palette) => void
	// Contrast
	contrast: Contrast
	setContrast: (contrast: Contrast) => void
	// Text size
	textSize: TextSize
	setTextSize: (size: TextSize) => void
}

// ── Helpers ────────────────────────────────────────────────────────

const PALETTE_OPTIONS: Palette[] = ['brand', 'default']
const TEXT_SIZE_OPTIONS: TextSize[] = ['small', 'medium', 'large']

function getSystemPrefersDark() {
	return window.matchMedia('(prefers-color-scheme: dark)').matches
}

// ── Context ────────────────────────────────────────────────────────

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// ── Provider ───────────────────────────────────────────────────────

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	// Dark mode state
	const [themeMode, setThemeModeState] = useState<Theme>(() => {
		const saved = localStorage.getItem('theme')
		if (saved === 'light' || saved === 'dark' || saved === 'system')
			return saved
		const legacy = localStorage.getItem('darkMode')
		if (legacy === 'true') return 'dark'
		return 'light'
	})

	const [systemDark, setSystemDark] = useState(getSystemPrefersDark)

	useEffect(() => {
		const mq = window.matchMedia('(prefers-color-scheme: dark)')
		const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches)
		mq.addEventListener('change', handler)
		return () => mq.removeEventListener('change', handler)
	}, [])

	const darkMode = themeMode === 'dark' || (themeMode === 'system' && systemDark)

	// Palette state
	const [palette, setPaletteState] = useState<Palette>(() => {
		const saved = localStorage.getItem('palette')
		if (saved && PALETTE_OPTIONS.includes(saved as Palette))
			return saved as Palette
		return 'brand'
	})

	// Contrast state
	const [contrast, setContrastState] = useState<Contrast>(() => {
		const saved = localStorage.getItem('contrast')
		if (saved === 'high' || saved === 'regular') return saved
		return 'regular'
	})

	// Text size state
	const [textSize, setTextSizeState] = useState<TextSize>(() => {
		const saved = localStorage.getItem('textSize')
		if (saved && TEXT_SIZE_OPTIONS.includes(saved as TextSize))
			return saved as TextSize
		return 'medium'
	})

	// ── Apply all theme changes to DOM in a single effect ──────────

	useEffect(() => {
		const root = document.documentElement

		// Dark mode
		localStorage.setItem('theme', themeMode)
		localStorage.removeItem('darkMode')
		root.classList.toggle('dark', darkMode)

		// Palette
		localStorage.setItem('palette', palette)
		if (palette === 'default') {
			root.setAttribute('data-palette', 'default')
		} else {
			root.removeAttribute('data-palette')
		}

		// Contrast
		localStorage.setItem('contrast', contrast)
		root.classList.toggle('hc', contrast === 'high')

		// Text size
		localStorage.setItem('textSize', textSize)
		root.classList.remove('text-small', 'text-large')
		if (textSize !== 'medium') {
			root.classList.add(`text-${textSize}`)
		}
	}, [themeMode, darkMode, palette, contrast, textSize])

	// ── Stable setters ─────────────────────────────────────────────

	const setTheme = useCallback((t: Theme) => setThemeModeState(t), [])
	const toggleDarkMode = useCallback(
		() => setThemeModeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
		[],
	)
	const setPalette = useCallback((p: Palette) => setPaletteState(p), [])
	const setContrast = useCallback((c: Contrast) => setContrastState(c), [])
	const setTextSize = useCallback((s: TextSize) => setTextSizeState(s), [])

	const value = useMemo(
		() => ({
			darkMode,
			toggleDarkMode,
			theme: themeMode,
			setTheme,
			palette,
			setPalette,
			contrast,
			setContrast,
			textSize,
			setTextSize,
		}),
		[darkMode, themeMode, toggleDarkMode, setTheme, palette, setPalette, contrast, setContrast, textSize, setTextSize],
	)

	return (
		<ThemeContext.Provider value={value}>
			{children}
		</ThemeContext.Provider>
	)
}

// ── Hooks ──────────────────────────────────────────────────────────

export const useTheme = () => {
	const context = useContext(ThemeContext)
	if (context === undefined) {
		throw new Error('useTheme must be used within a ThemeProvider')
	}
	return context
}

/** @deprecated Use useTheme() instead */
export const useDarkMode = () => {
	const { darkMode, toggleDarkMode, theme, setTheme } = useTheme()
	return { darkMode, toggleDarkMode, theme, setTheme }
}

/** @deprecated Use useTheme() instead */
export const usePalette = () => {
	const { palette, setPalette } = useTheme()
	return { palette, setPalette }
}

/** @deprecated Use useTheme() instead */
export const useContrast = () => {
	const { contrast, setContrast } = useTheme()
	return { contrast, setContrast }
}

/** @deprecated Use useTheme() instead */
export const useTextSize = () => {
	const { textSize, setTextSize } = useTheme()
	return { textSize, setTextSize }
}
